/**
 * Where the ladder blocks are in a whole program text that also holds SCL.
 *
 * The SCL validators must not read ladder text (a NETWORK line is not SCL), and
 * their findings must keep the program's line numbers. So masking blanks each
 * ladder block's lines and keeps every line break: the text is as long in lines
 * as it was, and a finding on line 40 is still about line 40.
 *
 * A block is found by its header line, the same test the file set and the
 * download name use (isLadderBlockHeader). A header inside a (* … *) comment
 * does not count.
 */
import { isLadderBlockHeader } from '@realplc/types';
/** A line that opens a block of any language. */
const BLOCK_HEADER = /^\s*(?:FUNCTION_BLOCK|FUNCTION|ORGANIZATION_BLOCK|DATA_BLOCK|TYPE|PROGRAM)\b/i;
function splitLines(text) {
    // Keeps each line's own terminator, so joining gives the text back byte for byte.
    return text.match(/[^\n]*\n|[^\n]+$/g) ?? [];
}
function lineBody(line) {
    return line.replace(/\r?\n$/, '');
}
/** For each line, whether it starts inside a (* … *) comment. */
function commentStateAtLineStarts(lines) {
    const states = [];
    let inComment = false;
    for (const line of lines) {
        states.push(inComment);
        let scan = lineBody(line);
        while (scan.length > 0) {
            const next = scan.indexOf(inComment ? '*)' : '(*');
            if (next < 0)
                break;
            inComment = !inComment;
            scan = scan.slice(next + 2);
        }
    }
    return states;
}
export function listLadBlocks(text) {
    const lines = splitLines(text);
    const inComment = commentStateAtLineStarts(lines);
    const blocks = [];
    for (let index = 0; index < lines.length; index += 1) {
        const body = lineBody(lines[index]);
        if (inComment[index] || !isLadderBlockHeader(body))
            continue;
        const kind = /^\s*(FUNCTION_BLOCK|ORGANIZATION_BLOCK)\b/i.exec(body)[1].toUpperCase();
        const name = /^\s*\w+\s+(?:"([^"\n]+)"|([A-Za-z_][A-Za-z0-9_]*))/.exec(body);
        const end = new RegExp(`^\\s*END_${kind}\\b`, 'i');
        let last = index;
        while (last + 1 < lines.length && !end.test(lineBody(lines[last]))) {
            // No END_ line: the block ends where the next one starts, as the
            // parser reads it, so the SCL after it is not masked away.
            if (!inComment[last + 1] && BLOCK_HEADER.test(lineBody(lines[last + 1])))
                break;
            last += 1;
        }
        blocks.push({
            kind,
            name: name?.[1] ?? name?.[2] ?? '',
            startLine: index + 1,
            endLine: last + 1,
            text: lines.slice(index, last + 1).join('').replace(/\r?\n$/, ''),
        });
        index = last;
    }
    return blocks;
}
export function maskLadBlocks(text) {
    const blocks = listLadBlocks(text);
    const lines = splitLines(text);
    for (const block of blocks) {
        for (let line = block.startLine; line <= block.endLine; line += 1) {
            lines[line - 1] = lines[line - 1].replace(/^[^\r\n]*/, '');
        }
    }
    return { text: lines.join(''), blocks };
}
/** Puts the ladder blocks back into a masked text whose lines have not moved. */
export function unmaskLadBlocks(masked) {
    const lines = splitLines(masked.text);
    for (const block of masked.blocks) {
        const original = splitLines(block.text);
        original.forEach((line, offset) => {
            const index = block.startLine - 1 + offset;
            const terminator = /\r?\n$/.exec(lines[index] ?? '')?.[0] ?? '';
            lines[index] = `${lineBody(line)}${terminator}`;
        });
    }
    return lines.join('');
}
//# sourceMappingURL=envelope.js.map